import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router";

import { getPartners, getPbxes, getPhoneNumbers } from "@/api/stackApi";
import ErrorBoundary from "@/app/shared/ErrorBoundary";
import { HELP_PHONE_NUMBERS } from "@/constants/HelpUrls";
import Avatar from "@/design/components/Avatar";
import FabBar from "@/design/components/FabBar";
import MessageStack from "@/design/components/MessageStack";
import Page from "@/design/layouts/Page";
import { setHelpUrl } from "@/store/reducers/ui";
import useDebounce from "@/utils/hooks/useDebounce";
import Valkyrie, {
  viCircleXmark,
  viCity,
  viMagnifyingGlass,
  viPhone,
  viPlus,
  viSimCard,
  viSmartphone,
  viStore,
} from "@sippy-platform/valkyrie";

import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Divider,
  InputAdornment,
  InputBase,
  Pagination,
} from "@mui/material";
import { useHotkey } from "@tanstack/react-hotkeys";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import ClearFilterAction from "../../domain/logs/_Components/ClearFilterAction";
import DataCell from "../../domain/logs/_Components/DataCell";
import FilterBar from "../../domain/logs/_Components/FilterBar";
import FilterDropdown from "../../domain/logs/_Components/FilterDropdown";
import TableHead from "../../domain/logs/_Components/TableHead";
import TableHeading from "../../domain/logs/_Components/TableHeading";
import TableRow from "../../domain/logs/_Components/TableRow";
import PhoneNumber from "./PhoneNumber";

export default function PhoneNumbers() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const auth = useSelector((state) => state.auth);

  useHotkey("Alt+N", () => navigate("/backstage/phone-numbers/new"));

  // Search field
  const [numberSearch, setNumberSearch] = useState(searchParams.get("number"));
  const debouncedNumber = useDebounce(numberSearch, 500);

  // Initialize
  useEffect(() => {
    dispatch(setHelpUrl(HELP_PHONE_NUMBERS));
  }, [dispatch]);

  // Filter preparations
  const pbxesQuery = useQuery({
    queryKey: ["pbxes"],
    queryFn: getPbxes,
    refetchInterval: 0,
  });

  const partnersQuery = useQuery({
    queryKey: ["partners"],
    queryFn: getPartners,
    refetchInterval: 0,
    enabled: auth.accessLevel === 0,
  });

  // Filters
  const { page, partner, pbx, number, type } = useMemo(
    () => ({
      page: Number(searchParams.get("page") ?? 0),
      partner: partnersQuery.data?.find((partner) => Number(searchParams.get("partner")) === partner.id)
        ? Number(searchParams.get("partner"))
        : (searchParams.get("partner") ?? ""),
      pbx: pbxesQuery.data?.find((pbx) => Number(searchParams.get("pbx")) === pbx.id)
        ? Number(searchParams.get("pbx"))
        : (searchParams.get("pbx") ?? ""),
      type: searchParams.get("type") || "",
      number: searchParams.get("number") || "",
    }),
    [searchParams, partnersQuery.data, pbxesQuery.data],
  );

  const isFiltering = useMemo(
    () => !(page === 0 && partner === "" && pbx === "" && number === "" && type === ""),
    [page, partner, pbx, number, type],
  );

  const setFilter = useCallback(
    (key, value, resetPage = true) => {
      searchParams.set(key, value);
      if (resetPage) searchParams.set("page", 0);

      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  // Query
  const phoneNumbersQuery = useQuery({
    queryKey: ["phonenumbers", page, partner, pbx, debouncedNumber, type],
    queryFn: () =>
      getPhoneNumbers({
        page,
        number: debouncedNumber,
        partner,
        pbx,
        type,
      }),
    placeholderData: keepPreviousData,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
  });

  // Handlers
  const handlePage = (event, page) => {
    setFilter("page", page - 1, false);
  };

  return (
    <Page sx={{ pt: 8, position: "relative" }}>
      <title>{`Phone numbers \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>

      <FilterBar>
        {auth.accessLevel === 0 && (
          <>
            <Button
              variant="contained"
              id="action-create"
              onClick={() => navigate("/backstage/phone-numbers/new")}
              startIcon={<Valkyrie icon={viPlus} />}
            >
              Add
            </Button>
            <Divider orientation="vertical" flexItem sx={{ my: 1, mx: 0.5 }} />
          </>
        )}
        <ClearFilterAction disabled={isFiltering} onClick={() => setNumberSearch("")} />
        <Divider orientation="vertical" flexItem sx={{ my: 1, mx: 0.5 }} />
        <InputBase
          type="search"
          placeholder="Phone number"
          onChange={(e) => {
            setNumberSearch(e.target.value);
            searchParams.set("number", e.target.value);
            searchParams.set("page", 0);
            setSearchParams(searchParams);
          }}
          value={numberSearch}
          sx={(theme) => ({
            width: 180,
            height: 36,
            flexShrink: 0,
            backgroundColor: theme.vars.palette.action.hover,
            color: "#000",
            borderRadius: 1,
            ...theme.applyStyles("dark", {
              color: "#fff",
            }),
            ".MuiInputAdornment-root": {
              pl: 1.5,
            },
            ".MuiInputBase-input": {
              py: 0,
              pl: 0,
              pr: 1.5,
              border: "none",
            },
          })}
          startAdornment={
            <InputAdornment position="start">
              <Valkyrie icon={viMagnifyingGlass} />
            </InputAdornment>
          }
        />
        <Divider orientation="vertical" flexItem sx={{ my: 1, mx: 0.5 }} />
        <FilterDropdown
          id="type"
          icon={viSimCard}
          defaultOptions={[
            { name: "All types", value: "" },
            { name: "Fixed", value: "fixed" },
            { name: "Mobile", value: "mobile" },
          ]}
          value={type}
          setValue={(value) => setFilter("type", value)}
          getOptionLabel={(x) => x.name}
          getOptionValue={(x) => x.value}
        />
        {auth.accessLevel === 0 && (
          <FilterDropdown
            id="partner"
            icon={viStore}
            options={partnersQuery.data || []}
            defaultOptions={[
              { name: "All partners", value: "" },
              { name: "Assigned", value: "assigned" },
              { name: "Unassigned", value: "unassigned" },
            ]}
            value={partner}
            setValue={(value) => setFilter("partner", value)}
            getOptionLabel={(x) => x.name}
            getOptionValue={(x) => x.id ?? x.value}
            getAvatarIcon={(x) => (x.id ? viStore : undefined)}
            searchKeys={["name"]}
          />
        )}
        <FilterDropdown
          id="pbx"
          icon={viCity}
          options={pbxesQuery.data || []}
          defaultOptions={[
            { name: "All PBXes", value: "" },
            { name: "Assigned", value: "assigned" },
            { name: "Unassigned", value: "unassigned" },
          ]}
          value={pbx}
          setValue={(value) => setFilter("pbx", value)}
          getOptionLabel={(x) => x.name}
          getOptionLabelSecondary={(x) => x.context}
          getOptionValue={(x) => x.id ?? x.value}
          getAvatarIcon={(x) => (x.id ? viCity : undefined)}
          searchKeys={["name", "context"]}
        />
      </FilterBar>
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(5, auto)",
            gridTemplateRows: "auto auto",
            gap: 1,
          }}
        >
          <TableHead>
            <TableHeading>Phone number</TableHeading>
            <TableHeading>Type</TableHeading>
            <TableHeading>Partner</TableHeading>
            <TableHeading>PBX</TableHeading>
          </TableHead>
          {phoneNumbersQuery.isFetched ? (
            <>
              {phoneNumbersQuery.data?.phoneNumbers?.length >= 1 ? (
                <Card sx={{ display: "grid", gridTemplateColumns: "subgrid", gridColumn: "1 / -1", mb: 6.5 }}>
                  {phoneNumbersQuery.data?.phoneNumbers.map((phoneNumber, numberKey) => (
                    <TableRow
                      key={numberKey}
                      component="button"
                      onClick={() => navigate(`/backstage/phone-numbers/${phoneNumber.did}?${searchParams.toString()}`)}
                      sx={{ "&:hover": { cursor: "pointer" } }}
                    >
                      <DataCell
                        startDecorator={<Avatar text={phoneNumber.did} px={24} icon={viSimCard} />}
                        primary={phoneNumber.did}
                      />
                      <DataCell
                        startDecorator={<Valkyrie icon={phoneNumber.type === "fixed" ? viPhone : viSmartphone} />}
                        primary={phoneNumber.type === "fixed" ? "Fixed" : "Mobile"}
                      />
                      <DataCell
                        startDecorator={
                          phoneNumber.partner ? (
                            <Avatar text={phoneNumber.partner.name} px={24} icon={viStore} />
                          ) : undefined
                        }
                        primary={phoneNumber.partner ? phoneNumber.partner.name : <i>Unassigned</i>}
                      />
                      <DataCell
                        startDecorator={phoneNumber.pbx ? <Avatar text={phoneNumber.pbx.name} px={24} /> : undefined}
                        primary={phoneNumber.pbx ? phoneNumber.pbx.name : <i>Unassigned</i>}
                      />
                    </TableRow>
                  ))}
                </Card>
              ) : (
                <MessageStack icon={<Valkyrie icon={viSimCard} />} message="No data for your current filters..." />
              )}
            </>
          ) : phoneNumbersQuery.isError ? (
            <MessageStack
              icon={<Valkyrie icon={viCircleXmark} />}
              color="error"
              message="We couldn't finish your request..."
              description={phoneNumbersQuery?.error?.message}
            />
          ) : (
            <MessageStack icon={<CircularProgress size={36} />} message="Getting your data..." />
          )}
        </Box>
      </Container>
      <ErrorBoundary>
        <PhoneNumber />
      </ErrorBoundary>
      {Math.ceil(phoneNumbersQuery.data?.total / 50) > 1 && (
        <FabBar sx={{ px: 1, justifyContent: "center" }}>
          <Pagination count={Math.ceil(phoneNumbersQuery.data?.total / 50)} page={page + 1} onChange={handlePage} />
        </FabBar>
      )}
    </Page>
  );
}
